import { useState, useMemo } from "react";
import { Panel, Label } from "@/components/SplitLayout";
import { toast } from "sonner";
import { ArrowSquareOut, Warning } from "@phosphor-icons/react";

const normalize = (raw) =>
  raw
    .split(/[\n,\s]+/)
    .map((u) => u.trim())
    .filter(Boolean)
    .map((u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`));

export default function BulkUrlOpener() {
  const [raw, setRaw] = useState("");

  const urls = useMemo(() => normalize(raw), [raw]);

  const openAll = () => {
    if (urls.length === 0) {
      toast.error("Add at least one URL first.");
      return;
    }
    let blocked = 0;
    urls.forEach((u) => {
      const w = window.open(u, "_blank", "noopener,noreferrer");
      if (!w) blocked++;
    });
    if (blocked > 0) {
      toast.warning(`${blocked} tab(s) were blocked. Allow pop-ups for this site.`);
    } else {
      toast.success(`Opened ${urls.length} tab(s).`);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <Label>URLs (one per line)</Label>
        <textarea
          data-testid="bulk-url-input"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={"example.com\nhttps://google.com\ngithub.com/explore"}
          className="h-[360px] w-full resize-none border border-border bg-card p-5 font-mono text-sm outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          data-testid="bulk-url-open"
          onClick={openAll}
          className="inline-flex w-full items-center justify-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-primary-foreground transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.98]"
        >
          <ArrowSquareOut size={16} weight="bold" /> Open all {urls.length} URL(s)
        </button>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start space-y-4">
        <Panel title={`Detected URLs · ${urls.length}`} testId="bulk-url-output">
          {urls.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Parsed URLs will be listed here.
            </p>
          ) : (
            <ul className="max-h-[260px] space-y-px overflow-auto">
              {urls.map((u, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 bg-background px-4 py-2.5 font-mono text-xs"
                >
                  <span className="text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                  <span className="truncate">{u}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <div className="flex items-start gap-3 border border-border bg-card p-4 text-xs text-muted-foreground">
          <Warning size={18} weight="duotone" className="mt-0.5 shrink-0 text-accent" />
          Your browser may block multiple tabs. If prompted, allow pop-ups for
          this site to open every URL at once.
        </div>
      </div>
    </div>
  );
}
